import React from 'react';
import './../../ff001-starstruck-lines/ff001-starstruck.css';
import { generateWindowFromReelStrips, getSymbolCountsFromWindow, wrap } from './../../../helpers';
import { Reel } from './../../reel/reel';
import { BigWin } from './../../big-win/big-win';

export interface IScenarioAlarmPosition {
  reelIndex: number;
  rowIndex: number;
}

export interface IScenarioVaultReveal {
  reelIndexes: number[];
  forceAlarms?: boolean;
  alarmPositions?: IScenarioAlarmPosition[];
}

export interface ISpinInfo {
  reelSetName: string;
  reelStrips: string[][];
  isFreeSpin: boolean;
  vaultActive: boolean;
  revealOnThisSpin: boolean;
  freeSpinsBefore: number;
  freeSpinsAfter: number;
  counts: { [key: string]: number };
}

function getBestIndexesForSymbol(props: { reelStrips: string[][]; rowsByReel: number[]; symbol: string }) {
  const { reelStrips, rowsByReel, symbol } = props;
  return reelStrips.map((reel, ri) => {
    let bestIndex = 0;
    let bestCount = -1;
    reel.forEach((_, index) => {
      let count = 0;
      for (let row = 0; row < rowsByReel[ri]; row++) {
        const wrapped = wrap(index + row, reel);
        if (reel[wrapped < 0 ? reel.length + wrapped : wrapped] === symbol) {
          count += 1;
        }
      }
      if (count > bestCount) {
        bestCount = count;
        bestIndex = index;
      }
    });
    return bestIndex;
  });
}

export const getValidAlarmPositions = (
  alarmPositions: IScenarioAlarmPosition[] | undefined,
  rowsByReel: number[]
) => {
  if (!Array.isArray(alarmPositions)) { return []; }
  const lastReelIndex = rowsByReel.length - 1;
  return alarmPositions.filter(position =>
    (position.reelIndex === 0 || position.reelIndex === lastReelIndex)
    && Number.isInteger(position.rowIndex)
    && position.rowIndex >= 0
    && position.rowIndex < rowsByReel[position.reelIndex]
  );
};

const applyForcedAlarmsToWindow = (props: {
  window: string[][];
  scenario: IScenarioVaultReveal;
  rowsByReel: number[];
  alarmSymbol: string;
}) => {
  if (!props.scenario.forceAlarms) { return props.window; }

  const alarmPositions = getValidAlarmPositions(props.scenario.alarmPositions, props.rowsByReel);
  if (alarmPositions.length === 0) { return props.window; }

  const newWindow = props.window.map(reel => [...reel]);
  alarmPositions.forEach(({ reelIndex, rowIndex }) => {
    newWindow[reelIndex][rowIndex] = props.alarmSymbol;
  });
  return newWindow;
};

function Spin(props: {
  config: any;
  scenario: IScenarioVaultReveal;
  index: number;
  spinInfo: ISpinInfo;
  onScenarioUpdated: (scenario: IScenarioVaultReveal, i: number) => unknown;
}) {
  const [window, setWindow] = React.useState<string[][]>([]);

  const updateScenario = (params: Partial<IScenarioVaultReveal>) => {
    const newScenario = { ...props.scenario, ...params };
    props.onScenarioUpdated(newScenario, props.index);
  };

  const setReelIndex = (params: { reelIndex: number; dir?: 1 | -1; forcedIndex?: number }) => {
    const { reelIndex, dir, forcedIndex } = params;
    const reelIndexes = props.scenario.reelIndexes;
    const reel = props.spinInfo.reelStrips[reelIndex];
    const n = (typeof forcedIndex === 'number') ? forcedIndex : reelIndexes[reelIndex] + (1 * dir!);
    const c = wrap(n, reel);
    const w = c < 0 ? (reel.length + c) : c;
    updateScenario({ reelIndexes: reelIndexes.map((x, i) => i === reelIndex ? w : x) });
  };

  const forceSymbol = (symbol: string) => {
    const reelIndexes = getBestIndexesForSymbol({
      reelStrips: props.spinInfo.reelStrips,
      rowsByReel: props.config.reelsSetup,
      symbol,
    });
    updateScenario({ reelIndexes });
  };

  const alarmPositions = getValidAlarmPositions(props.scenario.alarmPositions, props.config.reelsSetup);
  const lastReelIndex = props.config.reelsSetup.length - 1;
  const isAlarmPositionSelected = (reelIndex: number, rowIndex: number) =>
    alarmPositions.some(position => position.reelIndex === reelIndex && position.rowIndex === rowIndex);

  const toggleForceAlarms = () => {
    const forceAlarms = !props.scenario.forceAlarms;
    updateScenario({
      forceAlarms,
      alarmPositions: forceAlarms ? alarmPositions : [],
    });
  };

  const toggleAlarmPosition = (reelIndex: number, rowIndex: number) => {
    const selected = isAlarmPositionSelected(reelIndex, rowIndex);
    const newAlarmPositions = selected
      ? alarmPositions.filter(position => !(position.reelIndex === reelIndex && position.rowIndex === rowIndex))
      : [...alarmPositions, { reelIndex, rowIndex }];
    updateScenario({ forceAlarms: true, alarmPositions: newAlarmPositions });
  };

  React.useEffect(() => {
    const reels = props.config.reelsSetup.length;
    const rows = props.config.reelsSetup[0];
    const w0 = generateWindowFromReelStrips({
      reels,
      rows,
      reelIndexes: props.scenario.reelIndexes,
      reelStrips: props.spinInfo.reelStrips,
    });
    const w = props.spinInfo.isFreeSpin
      ? applyForcedAlarmsToWindow({
        window: w0,
        scenario: props.scenario,
        rowsByReel: props.config.reelsSetup,
        alarmSymbol: props.config.vaultAndReveal.alarmSymbol,
      })
      : w0;
    setWindow(w);
  }, [props.scenario.reelIndexes, props.scenario.forceAlarms, props.scenario.alarmPositions, props.spinInfo.isFreeSpin, props.spinInfo.reelStrips]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = getSymbolCountsFromWindow(window);
  const scat = counts[props.config.scat] || 0;
  const vault = counts[props.config.vaultAndReveal.symbol] || 0;
  const alarm = counts[props.config.vaultAndReveal.alarmSymbol] || 0;

  return <div className={`spin ${props.spinInfo.isFreeSpin ? 'spin-freespins' : ''}`}>
    <div className='spin-index-num'>{props.index}</div>

    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      <strong>{props.spinInfo.reelSetName}</strong>
      <span style={{ fontSize: 12, opacity: 0.85 }}>FS {props.spinInfo.freeSpinsBefore} {'->'} {props.spinInfo.freeSpinsAfter}</span>
      {props.spinInfo.vaultActive && <span style={{ fontSize: 12, color: '#ffed7a' }}>Vault active</span>}
      {props.spinInfo.revealOnThisSpin && <span style={{ fontSize: 12, color: '#80ffdb' }}>Reveal on settle</span>}
      <span style={{ fontSize: 12, opacity: 0.85 }}>scat {scat}</span>
      <span style={{ fontSize: 12, opacity: 0.85 }}>vault {vault}</span>
      <span style={{ fontSize: 12, opacity: 0.85 }}>alarm {alarm}</span>
    </div>

    <BigWin
      config={props.config}
      reelStrips={props.spinInfo.reelStrips}
      rowsByReel={props.config.reelsSetup}
      reelIndexes={props.scenario.reelIndexes}
      mode={props.spinInfo.isFreeSpin ? 'Free Spins' : 'Base'}
      showFreespins={!props.spinInfo.isFreeSpin}
      onApply={reelIndexes => updateScenario({ reelIndexes })}
    />
    <div className={'reelContainer'}>
      {props.scenario.reelIndexes.map((ri, i) => <Reel
        reelIndex={ri}
        setReelIndex={(props: { reelIndex: number; dir?: 1 | -1; forcedIndex?: number }) => setReelIndex(props)}
        reelStrips={props.spinInfo.reelStrips[i]}
        visibleSymbols={window[i]}
        index={i}
        rows={props.config.reelsSetup[i]}
        key={`reel-${props.index}-${i}-${props.spinInfo.reelSetName}`}
      />)}
    </div>

    <div className='btn-freespins-group' style={{ marginTop: 8, flexWrap: 'wrap', gap: 4 }}>
      <div className={'btn-freespins-action'} onClick={() => forceSymbol(props.config.vaultAndReveal.symbol)}>Force vaults</div>
      <div className={'btn-freespins-action'} onClick={() => forceSymbol(props.config.scat)}>Force scatters</div>
      {props.spinInfo.isFreeSpin && (
        <div
          className={'btn-freespins-action'}
          style={props.scenario.forceAlarms ? { background: '#ffed7a', color: '#171717' } : undefined}
          onClick={() => toggleForceAlarms()}
        >
          Force alarms
        </div>
      )}
    </div>

    {props.spinInfo.isFreeSpin && props.scenario.forceAlarms && (
      <div style={{ marginTop: 8, display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {[0, lastReelIndex].map(reelIndex => (
          <div key={`alarm-reel-${props.index}-${reelIndex}`} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 12 }}>Reel {reelIndex}</strong>
            {Array.from({ length: props.config.reelsSetup[reelIndex] }).map((_, rowIndex) => (
              <label key={`alarm-pos-${props.index}-${reelIndex}-${rowIndex}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={isAlarmPositionSelected(reelIndex, rowIndex)}
                  onChange={() => toggleAlarmPosition(reelIndex, rowIndex)}
                />
                Row {rowIndex}
              </label>
            ))}
          </div>
        ))}
      </div>
    )}
  </div>;
}

export default Spin;
